import type { FC } from "react";
import { useState } from "react";

import dompurify from "isomorphic-dompurify";

import { StoreLocator } from "@des/locators";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import type { FlotiqInStoreDeliveryStore, FlotiqStore } from "@jti/fe-common";
import type { Product } from "@jti/magento";
import { Dialog, useTranslations } from "@jti/ui";

import { useFeatureFlags } from "@commerce/shared/hooks";
import type { InStoreDeliveryShippingMethod } from "@commerce/types/CheckoutV2Configuration.types";
import { SelectedAddressView } from "@commerce/ui/SelectedAddressView";
import { SelectedAddressViewCard } from "@commerce/ui/SelectedAddressViewCard";

import styles from "./InStoreDeliveryStoreSelector.module.scss";

const { sanitize } = dompurify;

type InStoreDeliveryStoreSelectorProps = {
  onStoreSelect: (store: FlotiqInStoreDeliveryStore) => void;
  selectedStore?: FlotiqInStoreDeliveryStore | null;
  description?: string;
  storeLocatorConfiguration: Omit<InStoreDeliveryShippingMethod, "type" | "label" | "methodCode" | "description">;
  productsInCart?: Product[];
};

export const InStoreDeliveryStoreSelector: FC<InStoreDeliveryStoreSelectorProps> = ({
  productsInCart,
  selectedStore,
  onStoreSelect,
  description,
  storeLocatorConfiguration,
}) => {
  const { storeLocatorMapTheme = "dark" } = useConfig();
  const { __ } = useTranslations();
  const [isStoreLocatorOpen, setIsStoreLocatorOpen] = useState(false);
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const isStoreValidForPickup = (store: FlotiqStore): store is FlotiqInStoreDeliveryStore => {
    return !!(
      store.Status &&
      store.StoreName &&
      store.Street &&
      store.address &&
      store.ZipCode &&
      store.City &&
      store.Country &&
      store.id &&
      store.ExternalId
    );
  };

  const handleChooseStore = (store: FlotiqStore) => {
    if (!isStoreValidForPickup(store)) {
      toast.error(__("cart.checkout.delivery.in_store.invalid_store"));
      return;
    }

    onStoreSelect(store);
    closeStoreLocator();
  };

  const toggleStoreLocator = () => {
    setIsStoreLocatorOpen((state) => !state);
  };

  const closeStoreLocator = () => {
    setIsStoreLocatorOpen(false);
  };

  const shouldShowDescription = !!description && (!ffCheckoutV2EnableIteration2 || !selectedStore);

  const renderSelectedState = () => {
    if (!selectedStore) return null;

    if (ffCheckoutV2EnableIteration2) {
      return (
        <SelectedAddressViewCard
          title={__("cart.checkout.delivery.in_store.title")}
          changeBtnText={__("cart.checkout.delivery.in_store.change_store_cta")}
          onChange={toggleStoreLocator}
        >
          <SelectedAddressView
            title={selectedStore.StoreName}
            address={{
              street: selectedStore.Street,
              city: selectedStore.City,
              postCode: selectedStore.ZipCode,
            }}
          />
        </SelectedAddressViewCard>
      );
    }

    return (
      <SelectedAddressView
        title={selectedStore.StoreName}
        address={{
          street: selectedStore.Street,
          city: selectedStore.City,
          postCode: selectedStore.ZipCode,
        }}
      >
        <Button onClick={toggleStoreLocator} variant="text" data-testid="delivery-in-store-change-store-cta">
          {__("cart.checkout.delivery.in_store.change_store_cta")}
        </Button>
      </SelectedAddressView>
    );
  };

  return (
    <>
      {ffCheckoutV2EnableIteration2 && !selectedStore && (
        <p className={styles.title}>{__("cart.checkout.delivery.in_store.title")}</p>
      )}

      {shouldShowDescription && <div dangerouslySetInnerHTML={{ __html: sanitize(description || "") }} />}

      {!selectedStore ? (
        <Button onClick={toggleStoreLocator} variant="primary" data-testid="delivery-in-store-cta">
          {__("cart.checkout.delivery.in_store.cta")}
        </Button>
      ) : (
        renderSelectedState()
      )}

      <Dialog isOpened={isStoreLocatorOpen} onClose={closeStoreLocator} theme={storeLocatorMapTheme} fullscreen={true}>
        {isStoreLocatorOpen && (
          <StoreLocator
            {...(selectedStore ? { activeStoreId: selectedStore.id } : {})}
            defaultLng={String(selectedStore ? selectedStore.Location.lon : storeLocatorConfiguration.lon)}
            defaultLat={String(selectedStore ? selectedStore.Location.lat : storeLocatorConfiguration.lat)}
            flotiqApiKey={storeLocatorConfiguration.flotiqApiKey}
            flotiqContentType={storeLocatorConfiguration.flotiqContentType}
            storeDetailsPage={storeLocatorConfiguration.storeLocatorShopDetailsPage}
            defaultZoom={Number(storeLocatorConfiguration.defaultZoomLevel)}
            onChooseStore={handleChooseStore}
            filterByCartAvailabilityEnabled={storeLocatorConfiguration.filterByCartAvailabilityEnabled}
            withinCategoryFilterOperator="and"
            categoryAttributeFilter={storeLocatorConfiguration.categoryAttributeFilter}
            cartItems={storeLocatorConfiguration.enableStoreFilteringByAttributes ? productsInCart : undefined}
            isOpeningHoursEnabled={storeLocatorConfiguration.openingHoursEnabled}
            isOpeningHoursBreaksEnabled={storeLocatorConfiguration.openingHoursBreaksEnabled}
            isAvailableSticksEnabled={storeLocatorConfiguration.availableSticksEnabled}
            isAvailableDevicesEnabled={storeLocatorConfiguration.availableDevicesEnabled}
            filtersEnabled={storeLocatorConfiguration.filtersEnabled}
            isStoreCardAddressEnabled={storeLocatorConfiguration.storeCardAddressEnabled}
          />
        )}
      </Dialog>
    </>
  );
};
